/**
 * "On this day in history" (FE #94): every year a station holds for one
 * calendar date, as the view both apps render.
 *
 * Core owns the payload → formatted strings and decisions (ties, single
 * readings, gap runs, the normal, chart or list); the apps own the pixels.
 * Imports `../Dates` and `../Units` directly, never the utils barrel
 * (`importCycles.test.ts`).
 *
 * Values are the day's midnight-local `t_min` / `t_max` / `rr`. On a station
 * with official dailies (`clim_source === 'meteo_france'`) the `*_clim`
 * fields are Météo-France's published values, and as on the all-time records
 * card (`allTime.ts`) the more extreme of the two wins each year; a year with
 * no midnight rain takes the official (06-06 UTC) one. Everywhere else the
 * `*_clim` fields are our own rollup of the same readings and are not used.
 *
 * The records come from the years themselves, not from the payload's
 * `record` (the profile's, recomputed nightly): the chart, the table and the
 * headline then always agree.
 */
import {messages} from '@smartmixin/i18n';
import type {IntlShape} from 'react-intl';
import type {StationOnThisDayYear, StationRecords} from '../../api/types';
import * as Dates from '../Dates';
import * as Units from '../Units';
import {hasOfficialDailies} from './allTime';
import type {YearRangeMark, YearRangePoint} from './yearRangeChart';

type FormatMessage = IntlShape['formatMessage'];

export type MonthDay = {month: number; day: number};

/** The year-by-year chart needs this many drawn years; below, a list. */
export const CHART_MIN_YEARS = 10;

/** This many consecutive empty years fold into one table row. */
export const NO_DATA_RUN_MIN = 3;

/**
 * The WMO standard normal. Used when at least `minYears` of its years carry a
 * full range for the date; otherwise the normal is the period of record.
 */
export const STANDARD_NORMAL = {from: 1991, to: 2020, minYears: 15};

/** Any leap year: the picker offers February 29 every year. */
const LEAP_YEAR = 2000;

export const daysInPickerMonth = (month: number) =>
  Dates.daysInMonth(LEAP_YEAR, month);

/**
 * Whether a month and day exist on the calendar. The backend accepts day
 * 1-31 for any month, so February 30 would silently answer with no years:
 * never send one.
 */
export const isValidMonthDay = ({month, day}: MonthDay) =>
  Number.isInteger(month) &&
  month >= 1 &&
  month <= 12 &&
  Number.isInteger(day) &&
  day >= 1 &&
  day <= daysInPickerMonth(month);

/** Today in the station's zone, which is what the report opens on. */
export const todayMonthDay = (
  timezone: string,
  now: number = Dates.now(),
): MonthDay => {
  const local = Dates.timezoneDateFromTimestamp(now, timezone);
  return {month: local.month() + 1, day: local.date()};
};

const pad2 = (n: number) => String(n).padStart(2, '0');

/** The store key of one station's date: `30186:10-06`. */
export const onThisDayKey = (stationId: number, {month, day}: MonthDay) =>
  `${stationId}:${pad2(month)}-${pad2(day)}`;

export type OnThisDayHeadline = {
  value: string;
  unit: string;
  year: number;
  /** Other years with the same displayed value, newest first. */
  tied: number[];
};

export type OnThisDayNormal = {
  min: number;
  max: number;
  minLabel: string;
  maxLabel: string;
  basis: 'standard' | 'periodOfRecord';
  /** Years averaged. */
  years: number;
};

export type OnThisDayRow =
  | {kind: 'year'; year: number; min: string; max: string; rain?: string}
  | {kind: 'oneReading'; year: number; value: string; rain?: string}
  | {kind: 'noData'; year: number; rain?: string}
  | {kind: 'noDataRun'; from: number; to: number; count: number};

export type OnThisDayView = {
  state: 'ok' | 'emptyForDate' | 'noProfile';
  coverage?: {years: number; firstYear: number; rainSince?: number};
  headline: {
    high?: OnThisDayHeadline;
    low?: OnThisDayHeadline;
    wettest?: OnThisDayHeadline;
  };
  mode: 'chart' | 'list';
  normal?: OnThisDayNormal;
  /** Drawn years, oldest first, in the user's units. */
  series: YearRangePoint[];
  /** Years whose low equals their high: one reading that day, not drawn. */
  singleReadingYears: number[];
  /** Every year with a rain reading, oldest first, in the user's units. */
  rain: {year: number; value: number}[];
  /** The table, newest first. */
  rows: OnThisDayRow[];
  units: {temperature: string; rain: string};
  /** `records_since` as served (`YYYY-MM-DD`), for the empty state. */
  recordsSince?: string;
};

const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const hasRange = (y: StationOnThisDayYear) =>
  isNumber(y.t_min) && isNumber(y.t_max);

const isSingleReading = (y: StationOnThisDayYear) =>
  hasRange(y) && y.t_min === y.t_max;

const isEmpty = (y: StationOnThisDayYear) =>
  !isNumber(y.t_min) && !isNumber(y.t_max) && !isNumber(y.rr);

const moreExtreme = (
  a: number | undefined,
  b: number | undefined,
  pick: (...values: number[]) => number,
) => {
  const values = [a, b].filter(isNumber);
  return values.length ? pick(...values) : undefined;
};

/** One year as shown on a station with official dailies: see the header. */
const withOfficial = (y: StationOnThisDayYear): StationOnThisDayYear => {
  const t_min = moreExtreme(y.t_min, y.t_min_clim, Math.min);
  const t_max = moreExtreme(y.t_max, y.t_max_clim, Math.max);
  const rr = isNumber(y.rr) ? y.rr : y.rr_clim;
  return {
    year: y.year,
    ...(t_min !== undefined && {t_min}),
    ...(t_max !== undefined && {t_max}),
    ...(isNumber(rr) && {rr}),
  };
};

/**
 * The low and high a year's normal is averaged from: ONE series, never the
 * merged extremes, which are right for a record and biased outwards for a
 * mean. The midnight pair where it is a real range, else the official one on
 * a station that has it.
 */
const normalPair = (
  y: StationOnThisDayYear,
  official: boolean,
): {lo: number; hi: number} | undefined => {
  if (isNumber(y.t_min) && isNumber(y.t_max) && y.t_min < y.t_max) {
    return {lo: y.t_min, hi: y.t_max};
  }
  if (official && isNumber(y.t_min_clim) && isNumber(y.t_max_clim)) {
    return {lo: y.t_min_clim, hi: y.t_max_clim};
  }
  return undefined;
};

/** "°" after a Celsius or Fahrenheit value, " K" after a kelvin one. */
const degreeSuffix = (temperatureUnit: string) =>
  temperatureUnit.startsWith('°') ? '°' : ` ${temperatureUnit}`;

const mean = (values: number[]) =>
  values.reduce((sum, v) => sum + v, 0) / values.length;

type Field = 't_max' | 't_min' | 'rr';

const EMPTY_VIEW = (
  state: OnThisDayView['state'],
  units: OnThisDayView['units'],
  recordsSince?: string | null,
): OnThisDayView => ({
  state,
  headline: {},
  mode: 'list',
  series: [],
  singleReadingYears: [],
  rain: [],
  rows: [],
  units,
  ...(recordsSince ? {recordsSince} : {}),
});

export const onThisDayView = (
  data: StationRecords | undefined,
  {units}: {units: Units.Units},
): OnThisDayView => {
  const unitLabels = {
    temperature: Units.getTemperatureUnitLabel(units),
    rain: Units.getRainUnitLabel(units),
  };
  const section = data?.on_this_day;
  if (!section) {
    // A 204 caches as a bare `{fetched_at}`: no climate profile at all.
    return EMPTY_VIEW('noProfile', unitLabels);
  }
  const official = hasOfficialDailies(data);
  const entries = [...(section.years ?? [])]
    .map((y) => (official ? withOfficial(y) : y))
    .sort((a, b) => a.year - b.year);
  const firstWithData = entries.findIndex((y) => !isEmpty(y));
  if (firstWithData < 0) {
    return EMPTY_VIEW('emptyForDate', unitLabels, data?.records_since);
  }
  // Every calendar year from the first with data to the last entry: the
  // backend sends no entry for a year with no field at all, and the table
  // must fold those as the chart shades them.
  const byYear = new Map(entries.map((y) => [y.year, y]));
  const firstYear = entries[firstWithData].year;
  const lastYear = entries[entries.length - 1].year;
  const years: StationOnThisDayYear[] = [];
  for (let year = firstYear; year <= lastYear; year += 1) {
    years.push(byYear.get(year) ?? {year});
  }
  const withData = years.filter((y) => !isEmpty(y));

  const t = (v: number) => Units.convertValueToString(v, 'T', units);
  const r = (v: number) => Units.convertValueToString(v, 'RR', units);
  const tNumber = (v: number) => Units.convertValue(v, 'T', units, 1) as number;

  const headlineFor = (
    field: Field,
    pick: (a: number, b: number) => boolean,
    format: (v: number) => string,
    unit: string,
  ): OnThisDayHeadline | undefined => {
    // A temperature record comes from a year the table shows, low and high.
    const withValue = years.filter(
      (y) => isNumber(y[field]) && (field === 'rr' || hasRange(y)),
    );
    // The extreme, and the earliest year that reached it (the backend's
    // `idxmax` too).
    const best = withValue.reduce<StationOnThisDayYear | undefined>(
      (acc, y) => (!acc || pick(y[field]!, acc[field]!) ? y : acc),
      undefined,
    );
    if (!best) {
      return undefined;
    }
    const value = best[field]!;
    const year = best.year;
    // Ties as the reader sees them: two values that print the same are the
    // same record, whatever the raw decimals.
    const shown = format(value);
    const tied = withValue
      .filter((y) => y.year !== year && format(y[field]!) === shown)
      .map((y) => y.year)
      .sort((a, b) => b - a);
    return {value: shown, unit, year, tied};
  };

  const ranged = years.filter((y) => hasRange(y) && !isSingleReading(y));
  const series = ranged.map((y) => ({
    year: y.year,
    lo: tNumber(y.t_min!),
    hi: tNumber(y.t_max!),
  }));

  const asServed = new Map((section.years ?? []).map((y) => [y.year, y]));
  const pairs = ranged.flatMap((y) => {
    const pair = normalPair(asServed.get(y.year) ?? y, official);
    return pair ? [{year: y.year, ...pair}] : [];
  });
  const standard = pairs.filter(
    (y) => y.year >= STANDARD_NORMAL.from && y.year <= STANDARD_NORMAL.to,
  );
  const basisYears =
    standard.length >= STANDARD_NORMAL.minYears ? standard : pairs;
  const normal: OnThisDayNormal | undefined = basisYears.length
    ? (() => {
        const min = mean(basisYears.map((y) => y.lo));
        const max = mean(basisYears.map((y) => y.hi));
        return {
          min: tNumber(min),
          max: tNumber(max),
          minLabel: t(min),
          maxLabel: t(max),
          basis: basisYears === standard ? 'standard' : 'periodOfRecord',
          years: basisYears.length,
        };
      })()
    : undefined;

  const rainYears = years.filter((y) => isNumber(y.rr));
  const rainSince = rainYears.length ? rainYears[0].year : undefined;

  const rows: OnThisDayRow[] = [];
  const newestFirst = [...years].reverse();
  for (let i = 0; i < newestFirst.length; i += 1) {
    const y = newestFirst[i];
    if (isEmpty(y)) {
      let j = i;
      while (
        j + 1 < newestFirst.length &&
        isEmpty(newestFirst[j + 1]) &&
        newestFirst[j + 1].year === newestFirst[j].year - 1
      ) {
        j += 1;
      }
      if (j - i + 1 >= NO_DATA_RUN_MIN) {
        rows.push({
          kind: 'noDataRun',
          from: newestFirst[j].year,
          to: y.year,
          count: j - i + 1,
        });
        i = j;
        continue;
      }
    }
    const rain = isNumber(y.rr) ? {rain: r(y.rr)} : {};
    if (isSingleReading(y)) {
      rows.push({
        kind: 'oneReading',
        year: y.year,
        value: t(y.t_max!),
        ...rain,
      });
    } else if (hasRange(y)) {
      rows.push({
        kind: 'year',
        year: y.year,
        min: t(y.t_min!),
        max: t(y.t_max!),
        ...rain,
      });
    } else {
      rows.push({kind: 'noData', year: y.year, ...rain});
    }
  }

  const high = headlineFor('t_max', (a, b) => a > b, t, unitLabels.temperature);
  const low = headlineFor('t_min', (a, b) => a < b, t, unitLabels.temperature);
  const wettestRecord = headlineFor('rr', (a, b) => a > b, r, unitLabels.rain);
  // A date on which it never rained has no wettest day: "0.0 mm, tied with
  // every year" says nothing.
  const wettest =
    wettestRecord && rainYears.some((y) => (y.rr as number) > 0)
      ? wettestRecord
      : undefined;

  return {
    state: 'ok',
    coverage: {
      years: withData.length,
      firstYear,
      ...(rainSince !== undefined && rainSince > firstYear && {rainSince}),
    },
    headline: {
      ...(high && {high}),
      ...(low && {low}),
      ...(wettest && {wettest}),
    },
    mode: series.length >= CHART_MIN_YEARS ? 'chart' : 'list',
    normal,
    series,
    singleReadingYears: years.filter(isSingleReading).map((y) => y.year),
    rain: rainYears.map((y) => ({
      year: y.year,
      value: Units.convertValue(y.rr!, 'RR', units) as number,
    })),
    rows,
    units: unitLabels,
    ...(data?.records_since ? {recordsSince: data.records_since} : {}),
  };
};

/** "2001", "2001 & 2018", "2001, 2010 & 2018": the lists' own join. */
const joinYears = (years: number[]) =>
  years.length <= 1
    ? years.join('')
    : `${years.slice(0, -1).join(', ')} & ${years[years.length - 1]}`;

/**
 * The tie note beside a record: "tied 2018", "tied 2018 & 2001", and from
 * three years on two names and a count, "tied 2018, 2001 +1".
 */
export const formatTied = (years: number[], formatMessage: FormatMessage) => {
  if (years.length === 0) {
    return undefined;
  }
  if (years.length <= 2) {
    return formatMessage(messages.reports.onThisDayTied, {
      years: years.join(' & '),
    });
  }
  return formatMessage(messages.reports.onThisDayTiedMore, {
    years: years.slice(0, 2).join(', '),
    count: years.length - 2,
  });
};

/** The chart's accessible name: what it shows, in one paragraph. */
export const onThisDaySummary = (
  view: OnThisDayView,
  formatMessage: FormatMessage,
  units: Units.Units,
) => {
  const {high, low} = view.headline;
  const {normal, series} = view;
  if (view.state !== 'ok' || !series.length || !high || !low || !normal) {
    return undefined;
  }
  return formatMessage(messages.reports.onThisDayChartSummary, {
    years: series.length,
    first: series[0].year,
    last: series[series.length - 1].year,
    high: `${high.value} ${high.unit}`,
    highYears: joinYears([high.year, ...high.tied].sort((a, b) => a - b)),
    low: `${low.value} ${low.unit}`,
    lowYear: joinYears([low.year, ...low.tied].sort((a, b) => a - b)),
    min: normal.minLabel,
    max: `${normal.maxLabel} ${Units.getTemperatureUnitLabel(units)}`,
  });
};

const DASH = '–';

export type OnThisDayRowText =
  | {
      kind: 'year' | 'noData';
      year: string;
      /** Table cells, a dash where there is no value. */
      min: string;
      max: string;
      /** The short list's one cell: "14.4° / 25.0°", or a dash. */
      range: string;
      rain: string;
      /** "0.8 mm", or a dash. */
      rainWithUnit: string;
      /** The row as one sentence, for screen readers. */
      spoken: string;
    }
  | {
      kind: 'oneReading';
      year: string;
      /** The table's merged low-high cell: "22.4 · 1 reading". */
      value: string;
      range: string;
      rain: string;
      rainWithUnit: string;
      spoken: string;
    }
  | {kind: 'noDataRun'; text: string; spoken: string};

/**
 * Everything both apps write for one table or list row: the cells, the short
 * list's form and the sentence a screen reader hears ("2001: low 14.4 °C,
 * high 25.0 °C, rain 0.8 mm"), so the two apps cannot drift apart.
 */
export const onThisDayRowText = (
  view: OnThisDayView,
  row: OnThisDayRow,
  formatMessage: FormatMessage,
): OnThisDayRowText => {
  if (row.kind === 'noDataRun') {
    const text = formatMessage(messages.reports.onThisDayNoDataRun, {
      start: String(row.from),
      end: String(row.to),
      count: row.count,
    });
    return {kind: 'noDataRun', text, spoken: text};
  }
  const {temperature, rain: rainUnit} = view.units;
  const degree = degreeSuffix(temperature);
  const year = String(row.year);
  const rain = row.rain ?? DASH;
  const rainWithUnit =
    row.rain !== undefined ? `${row.rain} ${rainUnit}` : DASH;
  const spokenRain =
    row.rain !== undefined
      ? `${row.rain} ${rainUnit}`
      : formatMessage(messages.reports.onThisDayRainNotMeasured);
  if (row.kind === 'oneReading') {
    const oneReading = formatMessage(messages.reports.onThisDayOneReading);
    return {
      kind: 'oneReading',
      year,
      value: `${row.value} · ${oneReading}`,
      range: `${row.value}${degree} · ${oneReading}`,
      rain,
      rainWithUnit,
      spoken: formatMessage(messages.reports.onThisDayOneReadingSummary, {
        year,
        value: `${row.value} ${temperature}`,
        rain: spokenRain,
      }),
    };
  }
  if (row.kind === 'noData') {
    return {
      kind: 'noData',
      year,
      min: DASH,
      max: DASH,
      range: DASH,
      rain,
      rainWithUnit,
      spoken:
        row.rain !== undefined
          ? formatMessage(messages.reports.onThisDayRainOnlySummary, {
              year,
              rain: spokenRain,
            })
          : formatMessage(messages.common.labelValue, {
              label: year,
              value: formatMessage(messages.reports.onThisDayGapNoData),
            }),
    };
  }
  return {
    kind: 'year',
    year,
    min: row.min,
    max: row.max,
    range: `${row.min}${degree} / ${row.max}${degree}`,
    rain,
    rainWithUnit,
    spoken: formatMessage(messages.reports.onThisDayYearSummary, {
      year,
      min: `${row.min} ${temperature}`,
      max: `${row.max} ${temperature}`,
      rain: spokenRain,
    }),
  };
};

/**
 * One drawn year, read out when it is selected: "2001: low 14.4 °C, high
 * 25.0 °C, rain 0.8 mm". Undefined for a year the chart does not draw.
 */
export const onThisDayYearSummary = (
  view: OnThisDayView,
  year: number,
  formatMessage: FormatMessage,
) => {
  const row = view.rows.find(
    (candidate) => candidate.kind === 'year' && candidate.year === year,
  );
  return row ? onThisDayRowText(view, row, formatMessage).spoken : undefined;
};

/** "2001", "2001 & 2018", "2001 & 2018 +1": a mark label's years. */
const markYears = (years: number[]) =>
  years.length <= 2
    ? years.join(' & ')
    : `${years[0]} & ${years[1]} +${years.length - 2}`;

export type OnThisDayChartModel = {
  /** First and last year of the axis, shared by both charts. */
  x0: number;
  x1: number;
  temperature: {
    series: YearRangePoint[];
    normal?: {min: number; max: number};
    marks: YearRangeMark[];
  };
  /**
   * Rain as bars from 0. A measured dry year is a zero, not a gap; a date on
   * which it never rained has no rain chart at all (an empty series).
   */
  rain: {series: YearRangePoint[]; marks: YearRangeMark[]};
};

/**
 * What the two charts draw, for `yearRangeLayout`: the temperature bars with
 * the normal band and the record high/low marks ("25.0° · 2001 & 2018"), and
 * the rain bars with the wettest day's mark. Undefined unless the view is ok.
 */
export const onThisDayChartModel = (
  view: OnThisDayView,
): OnThisDayChartModel | undefined => {
  if (view.state !== 'ok' || !view.coverage) {
    return undefined;
  }
  const newest = view.rows[0];
  const x1 = newest.kind === 'noDataRun' ? newest.to : newest.year;
  const {high, low, wettest} = view.headline;
  const degree = degreeSuffix(view.units.temperature);
  const marks: YearRangeMark[] = [];
  if (high) {
    const years = [high.year, ...high.tied];
    marks.push({
      kind: 'max',
      years,
      value: Number(high.value),
      label: `${high.value}${degree} · ${markYears(years)}`,
    });
  }
  if (low) {
    const years = [low.year, ...low.tied];
    marks.push({
      kind: 'min',
      years,
      value: Number(low.value),
      label: `${low.value}${degree} · ${markYears(years)}`,
    });
  }
  const rainMarks: YearRangeMark[] = [];
  const wettestValue = wettest
    ? view.rain.find((r) => r.year === wettest.year)?.value
    : undefined;
  if (wettest && wettestValue !== undefined && wettestValue > 0) {
    const years = [wettest.year, ...wettest.tied];
    rainMarks.push({
      kind: 'rain',
      years,
      value: wettestValue,
      label: `${wettest.value} ${wettest.unit} · ${markYears(years)}`,
    });
  }
  return {
    x0: view.coverage.firstYear,
    x1,
    temperature: {
      series: view.series,
      normal: view.normal && {min: view.normal.min, max: view.normal.max},
      marks,
    },
    rain: {
      series: view.rain.some((r) => r.value > 0)
        ? view.rain.map((r) => ({year: r.year, lo: 0, hi: r.value}))
        : [],
      marks: rainMarks,
    },
  };
};

export type OnThisDayStrings = {
  /** "141 years since 1885 · rain since 1993", under the date button. */
  coverageLine?: string;
  /** "Every October 6 on record": the chart/table card and the caption. */
  everyTitle: string;
  /** "Rain on October 6". */
  rainTitle: string;
  /** The rain chart's accessible name, with the wettest day. */
  rainSummary: string;
  /** "Standard normal (1991-2020)" or "Period of record (4 years)". */
  normalBasis?: string;
  /** "Normal day: 9.8° to 17.1°", the band's legend. */
  normalLegend?: string;
  /** The note on the years the chart leaves out. */
  singleReading?: string;
  /** Why a short record is a list; none when no year has a temperature. */
  chartRule?: string;
  /** The line under each record: "2001 (tied 2018)", "2011 · rain since 1993". */
  headlineSub: {high?: string; low?: string; wettest?: string};
  /** The empty state: heading, then one paragraph. */
  emptyTitle: string;
  emptyDetail: string;
};

/**
 * Every sentence the report shows around the charts, composed once for both
 * apps. `date` is the picked day as `Dates.formatMonthDay` writes it.
 */
export const onThisDayStrings = (
  view: OnThisDayView,
  formatMessage: FormatMessage,
  date: string,
): OnThisDayStrings => {
  const {coverage, normal, headline} = view;
  const rainSince = coverage?.rainSince
    ? formatMessage(messages.reports.onThisDayRainSince, {
        year: String(coverage.rainSince),
      })
    : undefined;
  const join = (parts: (string | undefined)[]) =>
    parts.filter(Boolean).join(' · ');
  const sub = (record?: OnThisDayHeadline, extra?: string) => {
    if (!record) {
      return undefined;
    }
    const tied = formatTied(record.tied, formatMessage);
    return join([
      tied
        ? formatMessage(messages.reports.onThisDayYearTied, {
            year: String(record.year),
            tied,
          })
        : String(record.year),
      extra,
    ]);
  };
  const rainTitle = formatMessage(messages.reports.onThisDayRain, {date});
  const {wettest} = headline;
  const degree = degreeSuffix(view.units.temperature);
  const hasTemperature =
    view.series.length > 0 || view.singleReadingYears.length > 0;
  return {
    coverageLine: coverage
      ? join([
          formatMessage(messages.reports.onThisDayCoverage, {
            years: coverage.years,
            firstYear: String(coverage.firstYear),
          }),
          rainSince,
        ])
      : undefined,
    everyTitle: formatMessage(messages.reports.onThisDayEveryYear, {date}),
    rainTitle,
    rainSummary: wettest
      ? formatMessage(messages.reports.onThisDayRainSummary, {
          date,
          value: `${wettest.value} ${wettest.unit}`,
          years: joinYears(
            [wettest.year, ...wettest.tied].sort((a, b) => a - b),
          ),
        })
      : rainTitle,
    normalBasis: normal
      ? normal.basis === 'standard'
        ? formatMessage(messages.reports.climateNormalStandardLabel, {
            period: `${STANDARD_NORMAL.from}-${STANDARD_NORMAL.to}`,
          })
        : formatMessage(messages.reports.climateNormalPeriodOfRecord, {
            years: normal.years,
          })
      : undefined,
    normalLegend: normal
      ? formatMessage(messages.reports.onThisDayNormal, {
          min: `${normal.minLabel}${degree}`,
          max: `${normal.maxLabel}${degree}`,
        })
      : undefined,
    singleReading: view.singleReadingYears.length
      ? formatMessage(messages.reports.onThisDaySingleReading, {
          count: view.singleReadingYears.length,
        })
      : undefined,
    chartRule: hasTemperature
      ? formatMessage(messages.reports.onThisDayChartRule, {
          count: CHART_MIN_YEARS,
          date,
        })
      : undefined,
    headlineSub: {
      high: sub(headline.high),
      low: sub(headline.low),
      wettest: sub(wettest, rainSince),
    },
    emptyTitle: formatMessage(messages.reports.onThisDayNoYears, {date}),
    // Two sentences, one paragraph.
    emptyDetail: [
      formatMessage(messages.reports.onThisDayNoYearsDetail, {date}),
      view.recordsSince
        ? formatMessage(messages.reports.onThisDayDataSince, {
            date: Dates.parseString(view.recordsSince).format('ll'),
          })
        : undefined,
    ]
      .filter(Boolean)
      .join(' '),
  };
};

/**
 * Stage 2 of a cold read, from 3 s: why it is slow, and that leaving is fine.
 * `years` is the cached Records & climate `coverage.n_years`, when there is
 * one; without it the sentence names the station's history instead.
 */
export const onThisDaySlowText = (
  formatMessage: FormatMessage,
  date: string,
  years?: number | null,
) =>
  `${
    years
      ? formatMessage(messages.reports.onThisDaySlowLoad, {years, date})
      : formatMessage(messages.reports.onThisDaySlowLoadGeneric, {date})
  } ${formatMessage(messages.reports.onThisDayLeaveOk)}`;
